import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import Swal from "sweetalert2";
import "./ViewGarageLayer.css";
import { toast } from "react-toastify";
import GarageReviewsSection from "./GarageReviewSection";

const ViewGarageLayer = () => {
  const [garage, setGarage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const { id } = useParams();
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGarage = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/garages/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const json = await response.json();

        if (response.ok) {
          setGarage(json.data);
        } else {
          Swal.fire({
            title: "Error",
            text: "Failed to fetch garage details",
            icon: "error",
          });
          navigate("/garages");
        }
      } catch (error) {
        console.error("Error fetching garage:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to fetch garage details",
          icon: "error",
        });
        navigate("/garages");
      } finally {
        setLoading(false);
      }
    };

    fetchGarage();
  }, [id, token, navigate]);

  const getGarageStatusClass = (status) => {
    switch (status) {
      case "approved":
        return "bg-success";
      case "rejected":
        return "bg-danger";
      case "pending":
      default:
        return "bg-warning";
    }
  };

  const handleApprove = () => {
    Swal.fire({
      title: "Approve this garage?",
      text: "It will become visible and bookable for car owners.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, approve it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-success btn-sm ms-2",
        cancelButton: "btn btn-sm",
        popup: "my-custom-modal",
        title: "my-custom-title",
        icon: "my-custom-icon",
        content: "my-custom-text",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/garages/${id}/approve`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (response.ok) {
          setGarage((prev) => ({
            ...prev,
            status: "approved",
            rejection_reason: null,
          }));
          toast.success("Garage approved successfully.");
        } else {
          const json = await response.json();
          toast.error(json.errorMessage || "Failed to approve garage");
        }
      } catch (error) {
        console.error("Error approving garage:", error);
        toast.error("Failed to approve garage");
      }
    });
  };

  const handleReject = () => {
    Swal.fire({
      icon: "error",
      title: "Reject this garage",
      input: "text",
      inputLabel: "Rejection reason",
      inputPlaceholder: "e.g. Missing required documents",
      showCancelButton: true,
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-danger btn-sm ms-2",
        cancelButton: "btn btn-sm",
        title: "my-custom-title",
      },
      buttonsStyling: false,
      inputValidator: (value) => {
        if (!value || value.trim().length < 3) {
          return "Reason must be at least 3 characters";
        }
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/garages/${id}/reject`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ reason: result.value.trim() }),
          },
        );
        if (response.ok) {
          setGarage((prev) => ({
            ...prev,
            status: "rejected",
            rejection_reason: result.value.trim(),
          }));
          toast.success("Garage rejected.");
        } else {
          const json = await response.json();
          toast.error(json.errorMessage || "Failed to reject garage");
        }
      } catch (error) {
        console.error("Error rejecting garage:", error);
        toast.error("Failed to reject garage");
      }
    });
  };

  const handleStatusToggle = (garageId, newStatus) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${newStatus ? "activate" : "deactivate"} this account?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${newStatus ? "activate" : "deactivate"} it!`,
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-success btn-sm ms-2",
        cancelButton: "btn btn-sm",
        popup: "my-custom-modal",
        title: "my-custom-title",
        icon: "my-custom-icon",
        content: "my-custom-text",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      const formData = new FormData();
      formData.append("isActive", newStatus);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/garages/${garageId}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        if (response.ok) {
          setGarage((prev) => ({
            ...prev,
            owner: {
              ...prev.owner,
              isActive: newStatus,
            },
          }));

          toast.success("Garage status updated successfully.");
        } else {
          throw new Error("Failed to update status");
        }
      } catch (error) {
        console.error("Error updating garage status:", error);

        Swal.fire({
          title: "Error",
          text: "Failed to update garage status",
          icon: "error",
        });
      }
    });
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!garage) {
    return (
      <div className="alert alert-danger" role="alert">
        Garage not found.
      </div>
    );
  }

  return (
    <div>
      <div className="card h-100 p-0 radius-12 my-3">
        <div className="card-header bg-base py-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <h6 className="text-lg fw-semibold mb-0">Garage Information</h6>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span
                className={`badge text-capitalize ${getGarageStatusClass(garage.status)}`}
              >
                {garage.status}
              </span>
              {garage.status !== "approved" && (
                <button
                  onClick={handleApprove}
                  className="btn btn-sm btn-success d-inline-flex align-items-center gap-1"
                >
                  <Icon
                    icon="mdi:check-circle-outline"
                    className="text-lg line-height-1"
                  />{" "}
                  Approve
                </button>
              )}
              {garage.status !== "rejected" && (
                <button
                  onClick={handleReject}
                  className="btn btn-sm btn-danger d-inline-flex align-items-center gap-1"
                >
                  <Icon
                    icon="mdi:close-circle-outline"
                    className="text-lg line-height-1"
                  />{" "}
                  Reject
                </button>
              )}
              <div className="form-switch switch-success d-flex align-items-center gap-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id={`status-switch-${garage.id}`}
                  checked={garage.owner.isActive || false}
                  onChange={() =>
                    handleStatusToggle(garage.id, !garage.owner.isActive)
                  }
                />
                <label
                  className="form-check-label line-height-1 fw-medium text-secondary-light"
                  htmlFor="garage-status"
                >
                  {garage.owner.isActive ? "Active" : "Inactive"}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body p-24">
          {garage.status === "rejected" && garage.rejection_reason && (
            <div className="mb-3">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Rejection Reason
              </label>
              <p
                className="form-control-static mb-0 text-danger"
                style={{ fontSize: "14px" }}
              >
                {garage.rejection_reason}
              </p>
            </div>
          )}

          <div className="row g-3">
            <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Garage Name
              </label>
              <p
                className="form-control-static mb-0"
                style={{ fontSize: "14px" }}
              >
                {garage.garage_name}
              </p>
            </div>

            <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Address
              </label>
              <p
                className="form-control-static mb-0"
                style={{ fontSize: "14px" }}
              >
                {garage.address}
              </p>
            </div>

            <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Registration Number
              </label>
              <p
                className="form-control-static mb-0"
                style={{ fontSize: "14px" }}
              >
                {garage.business_registration_number}
              </p>
            </div>

            <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Coordinates
              </label>
              <p
                className="form-control-static mb-0"
                style={{ fontSize: "14px" }}
              >
                {garage.latitude != null && garage.longitude != null
                  ? `${Number(garage.latitude).toFixed(6)}, ${Number(garage.longitude).toFixed(6)}`
                  : "Not available"}
              </p>
            </div>

            <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Date Created
              </label>
              <p
                className="form-control-static mb-0"
                style={{ fontSize: "14px" }}
              >
                {garage.created_at
                  ? format(
                      new Date(garage.created_at),
                      "dd MMM, yyyy 'at' hh:mm a",
                    )
                  : "Not available"}
              </p>
            </div>

            <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Last Updated
              </label>
              <p
                className="form-control-static mb-0"
                style={{ fontSize: "14px" }}
              >
                {garage.updated_at
                  ? format(
                      new Date(garage.updated_at),
                      "dd MMM, yyyy 'at' hh:mm a",
                    )
                  : "Never updated"}
              </p>
            </div>

            <div className="col-12">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                About Us
              </label>
              <p
                className="form-control-static mb-0"
                style={{ fontSize: "14px" }}
              >
                {garage.about_us || "No description provided"}
              </p>
            </div>

            <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Emergency Service
              </label>
              <p className="form-control-static mb-0">
                <span
                  className="badge"
                  style={{
                    backgroundColor: garage.emergency_service
                      ? "#B9F2CE"
                      : "#E2E5E9",
                    color: garage.emergency_service ? "#176638" : "#454B54",
                    fontSize: "12px",
                  }}
                >
                  {garage.emergency_service ? "Offered" : "Not Offered"}
                </span>
              </p>
            </div>

            <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Towing Service
              </label>
              <p className="form-control-static mb-0">
                <span
                  className="badge"
                  style={{
                    backgroundColor: garage.towing_service
                      ? "#B9F2CE"
                      : "#E2E5E9",
                    color: garage.towing_service ? "#176638" : "#454B54",
                    fontSize: "12px",
                  }}
                >
                  {garage.towing_service ? "Offered" : "Not Offered"}
                </span>
              </p>
            </div>

            {/* Diagnosis Information */}
          {/*  <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Offers Diagnosis
              </label>
              <p className="form-control-static mb-0">
                <span
                  className="badge"
                  style={{
                    backgroundColor:
                      garage.offers_diagnosis === true ||
                      garage.offers_diagnosis === 1 ||
                      garage.offers_diagnosis === "1" ||
                      garage.offers_diagnosis === "true"
                        ? "#B9F2CE"
                        : "#E2E5E9",
                    color:
                      garage.offers_diagnosis === true ||
                      garage.offers_diagnosis === 1 ||
                      garage.offers_diagnosis === "1" ||
                      garage.offers_diagnosis === "true"
                        ? "#176638"
                        : "#454B54",
                    fontSize: "12px",
                  }}
                >
                  {garage.offers_diagnosis === true ||
                  garage.offers_diagnosis === 1 ||
                  garage.offers_diagnosis === "1" ||
                  garage.offers_diagnosis === "true"
                    ? "Offered"
                    : "Not Offered"}
                </span>
              </p>
            </div>

             <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Diagnosis Fee
              </label>

              <p
                className="form-control-static mb-0 d-flex align-items-center gap-2"
                style={{ fontSize: "14px" }}
              >
                {garage.diagnosis_fee != null && garage.diagnosis_fee !== "" ? (
                  garage.diagnosis_free_until &&
                  new Date(garage.diagnosis_free_until).setHours(0, 0, 0, 0) >=
                    new Date().setHours(0, 0, 0, 0) ? (
                    <>
                      <del className="text-muted">${garage.diagnosis_fee}</del>

                      <span
                        className="badge"
                        style={{
                          backgroundColor: "#B9F2CE",
                          color: "#176638",
                          fontSize: "12px",
                        }}
                      >
                        $0
                      </span>
                    </>
                  ) : (
                    `$${garage.diagnosis_fee}`
                  )
                ) : (
                  "Not available"
                )}
              </p>
            </div>

            <div className="col-md-4">
              <label
                className="form-label mb-0 text-secondary"
                style={{ fontSize: "12px" }}
              >
                Diagnosis Free Until
              </label>
              <p
                className="form-control-static mb-0"
                style={{ fontSize: "14px" }}
              >
                {garage.diagnosis_free_until
                  ? format(
                      new Date(garage.diagnosis_free_until),
                      "dd MMM, yyyy",
                    )
                  : "Not available"}
              </p>
            </div> */}
          </div>
        </div>
      </div>

      {/* Reviews: Published / Removed tabs with All / Flagged only filter */}
      <GarageReviewsSection garageId={garage.id} />

      <div className="card h-100 p-0 radius-12 my-3">
        <div className="card-header bg-base py-3">
          <h6 className="text-lg fw-semibold mb-0">Owner Information</h6>
        </div>
        <div className="card-body p-24">
          <div className="d-flex align-items-center justify-content-between mb-5">
            <div className="d-flex align-items-center">
              <img
                src={
                  garage.owner.image_url ||
                  "/assets/images/user-list/user-list1.png"
                }
                alt="Owner"
                className="w-60-px h-60-px rounded-circle flex-shrink-0 me-3 overflow-hidden"
              />
              <div>
                <h5 className="mb-1">{garage.owner.fullName}</h5>
                <p className="mb-0 text-muted">@{garage.owner.username}</p>
              </div>
            </div>
            <p className="form-control-static">
              <span
                className={`badge ${garage.owner.isActive ? "bg-success" : "bg-secondary"}`}
              >
                {garage.owner.isActive ? "Active" : "Inactive"}
              </span>
            </p>
          </div>
          <div className="row">
            <div className="mb-2 col-md-4">
              <label className="form-label fw-semibold">Email</label>
              <p className="form-control-static">{garage.owner.email}</p>
            </div>
            <div className="mb-2 col-md-4">
              <label className="form-label fw-semibold">Phone Number</label>
              <p className="form-control-static">
                {garage.owner.phoneNumber ? garage.owner.phoneNumber : "-"}
              </p>
            </div>
            <div className="mb-2 col-md-4">
              <label className="form-label fw-semibold">Member Since</label>
              <p className="form-control-static">
                {format(new Date(garage.owner.createdAt), "dd MMM, yyyy")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="garage-services-hours my-3">
        <div className="garage-services-hours__header">
          <h6>Services & Operating Hours</h6>
        </div>

        <div className="garage-services-hours__body">
          <section>
            <h6 className="garage-services-hours__title">Services Offered</h6>

            {garage.services?.length > 0 ? (
              <div className="garage-services-hours__services">
                {garage.services.map((service, index) => (
                  <span
                    key={
                      typeof service === "string"
                        ? `${service}-${index}`
                        : (service.id ?? index)
                    }
                    className="garage-services-hours__chip"
                  >
                    {typeof service === "string" ? service : service.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="garage-services-hours__empty">No services listed</p>
            )}
          </section>

          <section className="garage-services-hours__hours">
            <h6 className="garage-services-hours__title">Operating Hours</h6>

            {garage.operating_hours?.length > 0 ? (
              <div className="garage-services-hours__grid">
                {garage.operating_hours.map((hour, index) => (
                  <div
                    key={hour.id ?? `${hour.day}-${index}`}
                    className="garage-services-hours__day"
                  >
                    <span className="garage-services-hours__day-label">
                      {hour.day}
                    </span>

                    <span className="garage-services-hours__time">
                      {hour.is_closed
                        ? "Closed"
                        : `${hour.open_time} - ${hour.close_time}`}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="garage-services-hours__empty">
                No operating hours set
              </p>
            )}
          </section>
        </div>
      </div>

      {garage.gallery_urls && garage.gallery_urls.length > 0 && (
        <div className="card h-100 p-0 radius-12 my-3">
          <div className="card-header bg-base py-3">
            <h6 className="text-lg fw-semibold mb-0">Gallery</h6>
          </div>

          <div className="card-body p-24">
            <div className="d-flex flex-wrap gap-3">
              {garage.gallery_urls.map((url, index) => (
                <div
                  key={index}
                  className="border rounded overflow-hidden"
                  style={{ width: "140px", height: "140px" }}
                >
                  <img
                    src={url}
                    alt={`Gallery ${index + 1}`}
                    className="w-100 h-100"
                    style={{
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedImageIndex(index)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Image Preview Modal */}
          {selectedImageIndex !== null && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.75)",
                zIndex: 9999,
              }}
            >
              {/* Previous Image */}
              <button
                type="button"
                className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                disabled={selectedImageIndex === 0}
                style={{
                  position: "absolute",
                  left: "30px",
                  width: "45px",
                  height: "45px",
                  zIndex: 10000,
                  opacity: selectedImageIndex === 0 ? 0.5 : 1,
                  cursor: selectedImageIndex === 0 ? "not-allowed" : "pointer",
                }}
                onClick={() => {
                  if (selectedImageIndex > 0) {
                    setSelectedImageIndex((prev) => prev - 1);
                  }
                }}
              >
                <i className="ri-arrow-left-s-line fs-4" />
              </button>

              {/* Preview Image */}
              <img
                src={garage.gallery_urls[selectedImageIndex]}
                alt={`Gallery ${selectedImageIndex + 1}`}
                style={{
                  maxWidth: "85%",
                  maxHeight: "85%",
                  objectFit: "contain",
                }}
              />

              {/* Next Image */}
              <button
                type="button"
                className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                disabled={selectedImageIndex === garage.gallery_urls.length - 1}
                style={{
                  position: "absolute",
                  right: "30px",
                  width: "45px",
                  height: "45px",
                  zIndex: 10000,
                  opacity:
                    selectedImageIndex === garage.gallery_urls.length - 1
                      ? 0.5
                      : 1,
                  cursor:
                    selectedImageIndex === garage.gallery_urls.length - 1
                      ? "not-allowed"
                      : "pointer",
                }}
                onClick={() => {
                  if (selectedImageIndex < garage.gallery_urls.length - 1) {
                    setSelectedImageIndex((prev) => prev + 1);
                  }
                }}
              >
                <i className="ri-arrow-right-s-line fs-4" />
              </button>

              {/* Close Button */}
              <button
                type="button"
                className="btn btn-light rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  position: "absolute",
                  top: "25px",
                  right: "25px",
                  width: "40px",
                  height: "40px",
                  zIndex: 10000,
                }}
                onClick={() => setSelectedImageIndex(null)}
              >
                <i className="ri-close-line fs-4" />
              </button>

              {/* Image Counter */}
              <div
                className="text-white"
                style={{
                  position: "absolute",
                  bottom: "25px",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                {selectedImageIndex + 1} / {garage.gallery_urls.length}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewGarageLayer;
